import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, Plus, Pencil, Inbox, Copy, Trash2, Radio, Loader2, EyeOff, ClipboardList, Send, Link2, Lock, Unlock } from 'lucide-react';
import { SendSurveyDialog } from './SendSurveyDialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const FormsPage = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [surveyToSend, setSurveyToSend] = useState(null);

  const fetchForms = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/forms`, { headers: getAuthHeaders() });
      setForms(res.data);
    } catch {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const createForm = async () => {
    setBusyId('new');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/forms`, { title: 'Untitled Form' }, { headers: getAuthHeaders() });
      navigate(`/admin/retreat/forms/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create form');
      setBusyId(null);
    }
  };

  const createSurvey = async () => {
    setBusyId('survey');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/forms/survey-template`, {}, { headers: getAuthHeaders() });
      toast.success('Survey created from template — edit the questions, then send it');
      navigate(`/admin/retreat/forms/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create survey');
      setBusyId(null);
    }
  };

  const toggleClosed = async (form) => {
    setBusyId(form.id);
    try {
      await axios.put(`${BACKEND_URL}/api/retreat/forms/${form.id}`, { closed: !form.closed }, { headers: getAuthHeaders() });
      toast.success(form.closed ? 'Survey reopened' : 'Survey closed to new responses');
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const copySurveyLink = async (form) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/survey/${form.id}`);
      toast.success('Survey link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const setLive = async (form) => {
    if (!window.confirm(`Make "${form.title}" the live form on the public waitlist page? Any other live form will be taken offline.`)) return;
    setBusyId(form.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/forms/${form.id}/set-live`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set live');
    } finally {
      setBusyId(null);
    }
  };

  const takeOffline = async (form) => {
    if (!window.confirm(`Take "${form.title}" offline? The public waitlist page will show a "form unavailable" notice until another form goes live.`)) return;
    setBusyId(form.id);
    try {
      await axios.post(`${BACKEND_URL}/api/retreat/forms/${form.id}/take-offline`, {}, { headers: getAuthHeaders() });
      toast.success('Form taken offline');
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  const duplicate = async (form) => {
    setBusyId(form.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/forms/${form.id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success(`Duplicated as "${res.data.title}"`);
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete "${form.title}" and its ${form.submission_count} submission(s)? This cannot be undone.`)) return;
    setBusyId(form.id);
    try {
      await axios.delete(`${BACKEND_URL}/api/retreat/forms/${form.id}`, { headers: getAuthHeaders() });
      toast.success('Form deleted');
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-wine" /></div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="forms-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-7 w-7 text-wine" /> Forms
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Build registration forms for the waitlist page, or surveys you can send to every participant.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={createSurvey} disabled={busyId === 'survey'} variant="outline" data-testid="create-survey-btn"
            className="border-wine/40 text-wine hover:bg-wine-50">
            {busyId === 'survey' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-2" />}
            New Survey
          </Button>
          <Button onClick={createForm} disabled={busyId === 'new'} data-testid="create-form-btn"
            className="bg-gradient-to-r from-wine to-burgundy text-white hover:opacity-90">
            {busyId === 'new' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            New Form
          </Button>
        </div>
      </div>

      {forms.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-gray-500">
          No forms yet. Create your first form to replace the Google Form.
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {forms.map((form) => (
            <Card key={form.id} className={`transition-all ${form.is_live ? 'border-2 border-emerald-500 shadow-md' : 'border border-gray-200'}`}
              data-testid={`form-card-${form.id}`}>
              <CardContent className="p-5 md:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-lg font-semibold text-gray-900 truncate">{form.title}</h2>
                      {form.purpose === 'survey' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-wine-50 text-wine-700 border border-wine/20" data-testid={`survey-badge-${form.id}`}>
                          <ClipboardList className="h-3 w-3" /> SURVEY{form.closed ? ' · CLOSED' : ''}
                        </span>
                      )}
                      {form.is_live && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300"
                          data-testid={`live-badge-${form.id}`}>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
                          </span>
                          LIVE
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{form.description || 'No description'}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {form.fields?.length || 0} questions · {form.submission_count} {form.purpose === 'survey' ? 'response' : 'submission'}{form.submission_count === 1 ? '' : 's'}
                      {form.updated_at && ` · updated ${new Date(form.updated_at).toLocaleDateString()}`}
                      {form.last_sent_at && ` · sent to ${form.last_sent_count} on ${new Date(form.last_sent_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/retreat/forms/${form.id}`)}
                      data-testid={`edit-form-${form.id}`}>
                      <Pencil className="h-4 w-4 mr-1.5" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/retreat/forms/${form.id}/submissions`)}
                      data-testid={`submissions-form-${form.id}`}>
                      <Inbox className="h-4 w-4 mr-1.5" /> Submissions ({form.submission_count})
                    </Button>
                    {form.purpose === 'survey' ? (
                      <>
                        <Button size="sm" onClick={() => setSurveyToSend(form)} disabled={busyId === form.id || form.closed}
                          className="bg-gradient-to-r from-wine to-burgundy text-white" data-testid={`send-survey-${form.id}`}>
                          <Send className="h-4 w-4 mr-1.5" /> Send to Participants
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => copySurveyLink(form)} title="Copy public link" data-testid={`copy-survey-link-${form.id}`}>
                          <Link2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleClosed(form)} disabled={busyId === form.id}
                          title={form.closed ? 'Reopen survey' : 'Close survey'} data-testid={`toggle-closed-${form.id}`}>
                          {form.closed ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        </Button>
                      </>
                    ) : form.is_live ? (
                      <Button size="sm" variant="outline" onClick={() => takeOffline(form)} disabled={busyId === form.id}
                        className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`take-offline-${form.id}`}>
                        <EyeOff className="h-4 w-4 mr-1.5" /> Take Offline
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => setLive(form)} disabled={busyId === form.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`set-live-${form.id}`}>
                        <Radio className="h-4 w-4 mr-1.5" /> Set Live
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => duplicate(form)} disabled={busyId === form.id}
                      title="Duplicate" data-testid={`duplicate-form-${form.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(form)} disabled={busyId === form.id || form.is_live}
                      title={form.is_live ? 'Take offline before deleting' : 'Delete'}
                      className="text-red-600 hover:bg-red-50" data-testid={`delete-form-${form.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {surveyToSend && <SendSurveyDialog form={surveyToSend} onClose={() => setSurveyToSend(null)} onSent={fetchForms} />}
    </div>
  );
};

export default FormsPage;
